import { getRouteApi } from '@tanstack/react-router'
import { endOfDay, isWithinInterval, startOfDay } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CheckIcon from '@/assets/icons/check.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import ConfirmModal from '@/components/ConfirmModal'
import LinkText from '@/components/LinkText/LinkText'
import type { SelectOption } from '@/components/Select/Select'
import Table, { type TableAction, type Column } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import Tag from '@/components/Tag/Tag'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { toast } from '@/hooks/useToast'
import { studentsQueryOptions } from '@/queries/studentsQueryOptions'
import useSemesters from '@/queries/useSemesters'
import useAuthStore from '@/store/useAuthStore'
import { formatDate } from '@/utils/format-date'
import { getColumnWidth } from '@/utils/get-column-width'
import { getFilterValue } from '@/utils/get-filter-value'
import { getValue } from '@/utils/get-value'

import AbsenceExpandedRow from '../components/AbsenceExpandedRow'
import ManageAbsenceModal from '../components/ManageAbsenceModal'
import { type AbsencesFiltersKeys } from '../constants/absences-filters'
import { getAbsencesStatusTag } from '../constants/get-absences-status-tags'
import useMarkAsPresent from '../mutations/useMarkAsPresent'
import { absenceClassesQueryOptions } from '../queries/absenceClassesQueryOptions'
import useAbsences, { type Absence } from '../queries/useAbsences'
import useAbsencesCount from '../queries/useAbsencesCount'
import useAbsencesStatusOptions from '../queries/useAbsencesStatusOptions'
import { type AbsenceStatus } from '../types/absences-status'

const routeApi = getRouteApi('/_auth/absences/')

const AbsencesListView = () => {
  const { t } = useTranslation(['absences', 'common'])
  const {
    page,
    pageSize,
    semester,
    students,
    classes,
    status,
    dateAfter,
    dateBefore
  } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const { user } = useAuthStore()

  const {
    data: absences,
    isLoading: isAbsencesLoading,
    refetch: refetchAbsenceList
  } = useAbsences({
    semesterId: semester,
    classesId: classes?.map(getValue),
    status,
    studentsId: students?.map(getValue),
    dateAfter,
    dateBefore,
    page,
    pageSize
  })

  const { data: absencesCount, isLoading: isAbsencesCountLoading } =
    useAbsencesCount({
      semesterId: semester
    })

  const isLoading = isAbsencesLoading || isAbsencesCountLoading

  const { data: semesters, isLoading: isSemestersLoading } = useSemesters({
    hidePastYearsSemesters: true
  })
  const selectedSemester = semesters?.getSemester(semester)

  const isArchivedSemester = selectedSemester?.status === 'archived'

  const { data: statuses, isLoading: isStatusesLoading } =
    useAbsencesStatusOptions()

  const [isMarkAsPresentModalOpen, setIsMarkAsPresentModalOpen] =
    useState(false)
  const [currentAbsence, setCurrentAbsence] = useState<Absence>()
  const [isManageAbsenceModalOpen, setIsManageAbsenceModalOpen] =
    useState(false)

  const closeMarkAsPresentModal = () => {
    setIsMarkAsPresentModalOpen(false)
    setCurrentAbsence(undefined)
  }

  const { isMobile, isExtraLarge } = useScreenResolution()

  const { mutate: markAsPresent } = useMarkAsPresent({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('notification.successfully-marked-as-present')
      })
      refetchAbsenceList()
      setIsManageAbsenceModalOpen(false)
      closeMarkAsPresentModal()
    },
    onError: () => {
      toast({
        variant: 'success',
        title: t('notification.failed-marked-as-present')
      })
      closeMarkAsPresentModal()
    }
  })

  const tableColumns: Column<Absence>[] = [
    {
      key: 'student',
      title: t('header.student'),
      cellDataTestId: 'student-name',
      width: isMobile ? TABLE_CELL_WIDTH.XL : undefined,
      render: (value: Absence) => (
        <LinkText
          to="/students-and-classes/students/$studentId/details"
          params={{
            studentId: value.student.id
          }}
        >
          {value.student.name}
        </LinkText>
      )
    },
    {
      key: 'class',
      title: t('header.class'),
      cellDataTestId: 'class-name',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.SM),
      render: (value: Absence) =>
        value.student.studentClass && value.student.studentClass.id ? (
          <LinkText
            to="/students-and-classes/classes/$classId/details"
            params={{
              classId: value.student.studentClass.id
            }}
          >
            {value.student.studentClass.name}
          </LinkText>
        ) : (
          value.student.studentClass?.name
        )
    },
    {
      dataIndex: 'startDate',
      key: 'startDate',
      title: t('header.start-date'),
      cellDataTestId: 'start-date',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      render: formatDate
    },
    {
      dataIndex: 'endDate',
      key: 'endDate',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.LG),
      title: t('header.end-date'),
      cellDataTestId: 'end-date',
      render: formatDate
    },
    {
      dataIndex: 'affectedLessons',
      key: 'affectedLessons',
      width: getColumnWidth(isExtraLarge, TABLE_CELL_WIDTH.MD),
      title: t('header.affected-lesson'),
      cellDataTestId: 'affected-lesson'
    },
    {
      dataIndex: 'status',
      key: 'status',
      cellDataTestId: 'status',
      title: t('header.status'),
      width: 140,
      render: (value: AbsenceStatus) => <Tag {...getAbsencesStatusTag(value)} />
    }
  ]

  const pagination = usePagination(routeApi)

  const handleClearAll = () => {
    navigate({
      search: {
        semester,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const handleChangeFilter = (
    key: AbsencesFiltersKeys,
    value?: SelectOption<string> | SelectOption<string>[] | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const isDateInsideSelectedSemester = (date: Date) => {
    if (!selectedSemester) return
    return isWithinInterval(startOfDay(date), {
      start: startOfDay(selectedSemester.startDate),
      end: endOfDay(selectedSemester.endDate)
    })
  }

  const shouldDateBeDisabled = (date: Date) =>
    !isDateInsideSelectedSemester(date)

  const handleCloseManageAbsenceModal = () => {
    setIsManageAbsenceModalOpen(false)
    refetchAbsenceList()
  }

  const getSemestersOptions = () => {
    if (!semesters) return []

    if (user?.isSuperAdmin) {
      return semesters.semestersFromCurrentYearOptions
    }

    return [
      {
        label: semesters.currentSemester.name,
        value: semesters.currentSemester.id
      }
    ]
  }

  const semestersOptions = getSemestersOptions()

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemestersLoading,
        options: semestersOptions || [],
        semesterList: semesters?.list || [],
        value: semester,
        onChange: value => {
          navigate({
            search: {
              semester: value,
              page: 1,
              pageSize: DEFAULT_PAGE_SIZE
            }
          })
        }
      }
    },
    {
      label: t('label.student'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'student',
        multiple: true,
        value: students,
        queryOptions: search =>
          studentsQueryOptions({
            search,
            semesterId: semester
          }),
        dataTestId: 'student-filter',
        placeholder: t('label.student'),
        onChange: value => handleChangeFilter('students', value)
      }
    },
    {
      label: t('label.class'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'classId',
        dataTestId: 'class-filter',
        multiple: true,
        value: classes,
        queryOptions: search =>
          absenceClassesQueryOptions({
            search,
            semesterId: semester
          }),
        placeholder: t('label.class'),
        onChange: value => handleChangeFilter('classes', value)
      }
    },
    {
      label: t('label.start-date'),
      variant: 'date-range',
      size: 'wide',
      filterProps: {
        id: 'range',
        defaultMonth: selectedSemester?.startDate,
        value: {
          from: dateAfter ? new Date(dateAfter) : undefined,
          to: dateBefore ? new Date(dateBefore) : undefined
        },
        disabledDate: shouldDateBeDisabled,
        onChange: value => {
          handleChangeFilter('dateAfter', value?.from)
          handleChangeFilter('dateBefore', value?.to)
        }
      }
    },
    {
      label: t('label.status'),
      variant: 'multiselect',
      filterProps: {
        id: 'status',
        multiple: true,
        loading: isStatusesLoading,
        options: statuses || [],
        value: status,
        dataTestId: 'status-filter',
        placeholder: t('label.status'),
        onChange: value => handleChangeFilter('status', value)
      }
    }
  ]

  const isSomeFilterSelected = filters.some(
    filter =>
      (filter.filterProps.value &&
        filter.filterProps.id !== 'semester' &&
        filter.variant !== 'date-range') ||
      (filter.variant === 'date-range' &&
        (filter.filterProps.value?.to || filter.filterProps.value?.from))
  )

  const actions: (row: Absence) => TableAction<Absence>[] = row =>
    row.student.status !== 'archived' && !isArchivedSemester
      ? [
          {
            icon: <CheckIcon />,
            text: t('button.mark-as-present'),
            onClick: value => {
              setCurrentAbsence(value)
              setIsMarkAsPresentModalOpen(true)
            }
          },
          {
            icon: <EditIcon />,
            onClick: value => {
              setCurrentAbsence(value)
              setIsManageAbsenceModalOpen(true)
            },
            text: t('button.manage')
          }
        ]
      : []

  return (
    <BasicLayout
      header={t('header.absences')}
      moduleName={t('header.academics', { ns: 'common' })}
      breadcrumbs={[{ title: t('common:navigation.absences') }]}
    >
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
      />
      <Table
        id="absences-table"
        actions={actions}
        isLoading={isLoading}
        data={absences?.list || []}
        columns={tableColumns}
        count={absences?.count}
        totalCount={absencesCount}
        {...pagination}
        expandedRowRender={record => <AbsenceExpandedRow absence={record} />}
        caption={t('header.absences')}
      />

      <ManageAbsenceModal
        open={isManageAbsenceModalOpen}
        onOpenChange={setIsManageAbsenceModalOpen}
        absenceId={currentAbsence?.id}
        studentName={currentAbsence?.student.name}
        markAsPresent={() => setIsMarkAsPresentModalOpen(true)}
        onClose={handleCloseManageAbsenceModal}
      />

      <ConfirmModal
        dataTestId="confirm-mark-as-present-modal"
        header={t('header.you-want-to-mark-absences-on-present')}
        description={`${currentAbsence?.student.name}, ${formatDate(currentAbsence?.startDate)} - ${formatDate(currentAbsence?.endDate)}`}
        subheader={
          <>
            <p>{t('text.will-remove-absence-from-attendance')}</p>
            <p>{t('text.action-can-not-be-undone')}</p>
          </>
        }
        onConfirm={() => {
          if (!currentAbsence) return
          markAsPresent({ id: currentAbsence?.id })
        }}
        open={isMarkAsPresentModalOpen}
        onOpenChange={setIsMarkAsPresentModalOpen}
      />
    </BasicLayout>
  )
}
export default AbsencesListView
